import type { ReactNode } from 'react';

import { PAYMENT_TYPES } from '@/lib/checkout/payments/types';

import VisaLogo from '@/components/ui/icons/payment/svg/visa.svg';
import MastercardLogo from '@/components/ui/icons/payment/svg/mastercard.svg';
import AmexLogo from '@/components/ui/icons/payment/svg/americanexpress.svg';
import DiscoverLogo from '@/components/ui/icons/payment/svg/discover.svg';
import DinersclubLogo from '@/components/ui/icons/payment/svg/dinersclub.svg';
import JcbLogo from '@/components/ui/icons/payment/svg/jcb.svg';
import UnionpayLogo from '@/components/ui/icons/payment/svg/unionpay.svg';
import PaypalLogo from '@/components/ui/icons/payment/svg/paypal.svg';
import ApplepayLogo from '@/components/ui/icons/payment/svg/applepay.svg';
import GooglepayLogo from '@/components/ui/icons/payment/svg/google-pay.svg';
import AffirmLogo from '@/components/ui/icons/payment/svg/affirm.svg';
import KlarnaLogo from '@/components/ui/icons/payment/svg/klarna.svg';
import IdealLogo from '@/components/ui/icons/payment/svg/ideal.svg';
import BancontactLogo from '@/components/ui/icons/payment/svg/bancontact.svg';
import AlipayLogo from '@/components/ui/icons/payment/svg/alipay.svg';
import OneyLogo from '@/components/ui/icons/payment/svg/oney.svg';
import CembraLogo from '@/components/ui/icons/payment/svg/cembra.svg';
import CreditPlusLogo from '@/components/ui/icons/payment/svg/credit-plus.svg';
import AgosLogo from '@/components/ui/icons/payment/svg/agos.svg';
import SmbcLogo from '@/components/ui/icons/payment/svg/smbc.svg';
import TwintLogo from '@/components/ui/icons/payment/svg/twint.svg';
import V12Logo from '@/components/ui/icons/payment/svg/v12.svg';
import ZipLogo from '@/components/ui/icons/payment/svg/zip.svg';
import PaybybankLogo from '@/components/ui/icons/payment/svg/paybybank-us.svg';

export interface PaymentMethodLogo {
  img: ReactNode;
  type: string;
}

export type GatewayPaymentMethod = {
  type: string;
  dropInConfiguration?: { brands?: string[] } | null;
};

type CardBrand = 'visa' | 'mc' | 'amex' | 'cup' | 'discover' | 'diners' | 'jcb';

type SvgLogoComponent = React.ComponentType<{
  className?: string;
  'aria-label'?: string;
}>;

interface PaymentLogoConfig {
  Component: SvgLogoComponent;
  ariaLabel: string;
  dedupKey?: string;
  renderKey?: string;
}

/** One-to-one payment type → logo mappings. */
const SIMPLE_PAYMENT_LOGOS: Partial<Record<string, PaymentLogoConfig>> = {
  [PAYMENT_TYPES.PAYPAL]: { Component: PaypalLogo, ariaLabel: 'PayPal' },
  [PAYMENT_TYPES.AFFIRM]: { Component: AffirmLogo, ariaLabel: 'Affirm' },
  [PAYMENT_TYPES.KLARNA_PAYNOW]: { Component: KlarnaLogo, ariaLabel: 'Klarna' },
  [PAYMENT_TYPES.IDEAL]: { Component: IdealLogo, ariaLabel: 'iDEAL' },
  [PAYMENT_TYPES.BANCONTACT]: { Component: BancontactLogo, ariaLabel: 'Bancontact' },
  [PAYMENT_TYPES.ALIPAY]: { Component: AlipayLogo, ariaLabel: 'Alipay' },
  [PAYMENT_TYPES.UNION_PAY]: { Component: UnionpayLogo, ariaLabel: 'UnionPay' },
  [PAYMENT_TYPES.CREDIT_PLUS]: { Component: CreditPlusLogo, ariaLabel: 'Credit Plus' },
  [PAYMENT_TYPES.AGOS]: { Component: AgosLogo, ariaLabel: 'Agos' },
  [PAYMENT_TYPES.SMBC]: { Component: SmbcLogo, ariaLabel: 'SMBC' },
  [PAYMENT_TYPES.TWINT]: { Component: TwintLogo, ariaLabel: 'TWINT' },
  [PAYMENT_TYPES.V12]: { Component: V12Logo, ariaLabel: 'V12' },
  [PAYMENT_TYPES.ZIP]: { Component: ZipLogo, ariaLabel: 'Zip' },
};

/** Payment types that share one logo and deduplicate under a common key. */
const GROUPED_PAYMENT_LOGOS: Array<PaymentLogoConfig & { types: string[] }> = [
  {
    types: [PAYMENT_TYPES.GOOGLE_PAY, PAYMENT_TYPES.PAY_WITH_GOOGLE],
    Component: GooglepayLogo,
    ariaLabel: 'Google Pay',
    dedupKey: 'googlepay',
  },
  {
    types: [PAYMENT_TYPES.FACILYPAY_3X, PAYMENT_TYPES.FACILYPAY_4X],
    Component: OneyLogo,
    ariaLabel: 'Oney',
    dedupKey: 'oney',
  },
  {
    types: [PAYMENT_TYPES.CEMBRA, PAYMENT_TYPES.CEMBRA_INVOICE],
    Component: CembraLogo,
    ariaLabel: 'Cembra Pay',
    dedupKey: 'cembra',
    renderKey: 'cembra',
  },
  {
    types: [PAYMENT_TYPES.PAYBYBANK, PAYMENT_TYPES.PAYBYBANK_US],
    Component: PaybybankLogo,
    ariaLabel: 'Pay by Bank',
    dedupKey: 'paybybank',
  },
];

const GROUPED_PAYMENT_LOGO_BY_TYPE = new Map(
  GROUPED_PAYMENT_LOGOS.flatMap((config) => config.types.map((type) => [type, config] as const))
);

function renderPaymentLogo(
  config: PaymentLogoConfig,
  className: string,
  methodType: string
): ReactNode {
  const { Component, ariaLabel, renderKey } = config;
  return <Component key={renderKey ?? methodType} className={className} aria-label={ariaLabel} />;
}

function addPaymentLogo(
  logos: PaymentMethodLogo[],
  addedTypes: Set<string>,
  methodType: string,
  config: PaymentLogoConfig,
  className: string
): void {
  const dedupKey = config.dedupKey ?? methodType;
  if (addedTypes.has(dedupKey)) return;

  logos.push({
    type: methodType,
    img: renderPaymentLogo(config, className, methodType),
  });
  addedTypes.add(dedupKey);
}

function getCardBrandLogo(brand: CardBrand, className: string): PaymentMethodLogo | null {
  switch (brand) {
    case 'visa':
      return {
        type: brand,
        img: <VisaLogo key={brand} className={className} aria-label="Visa" />,
      };
    case 'mc':
      return {
        type: brand,
        img: <MastercardLogo key={brand} className={className} aria-label="Mastercard" />,
      };
    case 'amex':
      return {
        type: brand,
        img: <AmexLogo key={brand} className={className} aria-label="American Express" />,
      };
    case 'discover':
      return {
        type: brand,
        img: <DiscoverLogo key={brand} className={className} aria-label="Discover" />,
      };
    case 'diners':
      return {
        type: brand,
        img: <DinersclubLogo key={brand} className={className} aria-label="Diners Club" />,
      };
    case 'jcb':
      return {
        type: brand,
        img: <JcbLogo key={brand} className={className} aria-label="JCB" />,
      };
    case 'cup':
      return {
        type: brand,
        img: <UnionpayLogo key={brand} className={className} aria-label="UnionPay" />,
      };
    default:
      return null;
  }
}

/** Convert gateway payment methods to logo components. */
export function getPaymentMethodsLogos(
  paymentMethods: GatewayPaymentMethod[],
  isApplePayAvailable: boolean,
  className: string
): PaymentMethodLogo[] {
  const logos: PaymentMethodLogo[] = [];
  const addedTypes = new Set<string>();

  for (const method of paymentMethods) {
    if (method.type === 'scheme' && method.dropInConfiguration?.brands) {
      for (const brand of method.dropInConfiguration.brands) {
        if (!addedTypes.has(brand)) {
          const logo = getCardBrandLogo(brand as CardBrand, className);
          if (logo) {
            logos.push(logo);
            addedTypes.add(brand);
          }
        }
      }
      continue;
    }

    if (addedTypes.has(method.type)) continue;

    if (method.type === PAYMENT_TYPES.APPLE_PAY) {
      if (isApplePayAvailable) {
        addPaymentLogo(
          logos,
          addedTypes,
          method.type,
          {
            Component: ApplepayLogo,
            ariaLabel: 'Apple Pay',
          },
          className
        );
      }
      continue;
    }

    const groupedLogo = GROUPED_PAYMENT_LOGO_BY_TYPE.get(method.type);
    if (groupedLogo) {
      addPaymentLogo(logos, addedTypes, method.type, groupedLogo, className);
      continue;
    }

    const simpleLogo = SIMPLE_PAYMENT_LOGOS[method.type];
    if (simpleLogo) {
      addPaymentLogo(logos, addedTypes, method.type, simpleLogo, className);
    }
  }

  return logos;
}
